import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ChevronLeft, 
  MapPin, 
  BookOpen, 
  Clock
} from 'lucide-react';
import { prophetApi } from '../services/api';
import { 
  PageLoading, 
  GlassCard,
  CertaintyBadge,
  ContentBadge,
  ArabicVerse
} from '../components/shared';
import RichTextRenderer from '../components/RichTextRenderer';

const ProphetDetail = () => {
  const { t } = useTranslation();
  const { prophetId } = useParams();
  const [prophet, setProphet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProphet = async () => {
      try {
        const response = await prophetApi.getProphet(prophetId);
        setProphet(response.data);
      } catch (error) {
        console.error('Error fetching prophet:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProphet();
  }, [prophetId]);

  if (loading) return <PageLoading />;
  if (!prophet) return <div className="text-center py-20 text-text-muted">{t('prophets.not_found')}</div>;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div 
        className="relative py-20 px-4 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1683138155815-d7edd806d8a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxkZXNlcnQlMjBuaWdodCUyMHNreSUyMHN0YXJzfGVufDB8fHx8MTc3NTQ2OTA2MXww&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background" />
        <div className="relative z-10 max-w-4xl mx-auto">
          <Link to="/prophets" className="inline-flex items-center text-text-muted hover:text-primary mb-6 transition-colors">
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('prophets.back_to_list')}
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm">
              {t('prophets.prophet_n', { order: prophet.order })}
            </span>
            <CertaintyBadge level={prophet.certainty_level} />
          </div>
          
          <h1 className="font-arabic text-5xl sm:text-6xl text-primary mb-4" dir="rtl">
            {prophet.name_arabic}
          </h1>
          <h2 className="text-3xl sm:text-4xl font-serif text-text-primary mb-2">
            {prophet.name_french || prophet.name_english}
          </h2>
          <p className="text-lg text-text-secondary">{prophet.name_english}</p>
          
          <div className="flex flex-wrap gap-4 mt-6 text-sm text-text-muted">
            {prophet.era && (
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {prophet.era}
              </span>
            )}
            {prophet.location && (
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {prophet.location}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Summary */}
            <GlassCard>
              <h3 className="font-serif text-xl text-text-primary mb-4">{t('prophets.about')}</h3>
              <RichTextRenderer
                value={prophet.summary}
                testid="prophet-summary"
                className="text-text-secondary leading-relaxed"
              />
            </GlassCard>

            {/* Related Verses */}
            {prophet.related_verses_data && prophet.related_verses_data.length > 0 && (
              <GlassCard>
                <h3 className="font-serif text-xl text-text-primary mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  {t('prophets.quran_mentions')}
                </h3>
                <div className="space-y-4">
                  {prophet.related_verses_data.map((verse, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="p-4 bg-surface-elevated rounded-lg"
                    >
                      <ContentBadge type="quran" className="mb-2" />
                      <p className="font-arabic text-xl text-white text-right mb-3" dir="rtl">
                        {verse.arabic_text}
                      </p>
                      <p className="text-sm text-text-secondary">{verse.translation_french || verse.translation_english}</p>
                      <Link 
                        to={`/quran/${verse.surah_number}`}
                        className="text-xs text-primary hover:underline mt-2 inline-block"
                      >
                        {t('prophets.see_surah', { surah: verse.surah_number, verse: verse.verse_number })}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </GlassCard>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quran References */}
            {prophet.quran_mentions && prophet.quran_mentions.length > 0 && (
              <GlassCard>
                <h4 className="font-serif text-lg text-text-primary mb-4">{t('prophets.quran_references')}</h4>
                <div className="flex flex-wrap gap-2">
                  {prophet.quran_mentions.map((ref, i) => (
                    <span 
                      key={i}
                      className="px-2 py-1 text-xs rounded bg-primary/10 text-primary"
                    >
                      {ref}
                    </span>
                  ))}
                </div>
              </GlassCard>
            )}

            {/* Era */}
            {prophet.era && (
              <GlassCard>
                <h4 className="text-sm font-medium text-text-muted mb-2">{t('prophets.era')}</h4>
                <p className="text-text-primary">{prophet.era}</p>
              </GlassCard>
            )}

            {/* Location */}
            {prophet.location && (
              <GlassCard>
                <h4 className="text-sm font-medium text-text-muted mb-2">{t('prophets.location')}</h4>
                <p className="text-text-primary">{prophet.location}</p>
              </GlassCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProphetDetail;
